import { api, ApiError } from '../api';
import { fmtCount } from '../lib/format';
import { Icon, type IconName } from './Icon';
import { useDialog } from './Dialog';
import type { Folder, SpecialUse } from '../types';

interface Props {
  folders: Folder[];
  selected: string;
  onSelect: (path: string) => void;
  onCompose: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  /** `null` — server kvota vermir, göstərici gizlədilir. */
  quota: { usedBytes: number; limitBytes: number } | null;
  /** Qovluq yaradıldıqdan/silindikdən sonra siyahını yeniləmək üçün. */
  onFoldersChanged: () => void;
  onError: (msg: string) => void;
}

/**
 * Sistem qovluqlarının azərbaycanca adları.
 *
 * ⚠️ İXRAC OLUNUR — mobil başlıq da eyni adı göstərməlidir. Əvvəl orada
 * xam IMAP adı («INBOX») görünürdü, yan paneldə isə «Gələnlər»; eyni
 * qovluq iki fərqli adla adlanırdı.
 */
export const FOLDER_LABELS: Record<SpecialUse, string> = {
  inbox: 'Gələnlər',
  sent: 'Göndərilənlər',
  drafts: 'Qaralamalar',
  archive: 'Arxiv',
  junk: 'Spam',
  trash: 'Səbət',
};

const ICONS: Record<SpecialUse, IconName> = {
  inbox: 'inbox',
  sent: 'send',
  drafts: 'draft',
  archive: 'archive',
  junk: 'spam',
  trash: 'trash',
};

/** Bayt sayını qısa oxunaqlı formada verir. */
function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
}

function label(f: Folder): string {
  return f.specialUse ? FOLDER_LABELS[f.specialUse] : f.name;
}

export function FolderList({
  folders,
  selected,
  onSelect,
  onCompose,
  onRefresh,
  refreshing,
  quota,
  onFoldersChanged,
  onError,
}: Props): React.ReactElement {
  // ⚠️ HOOK VALİDEYNDƏDİR, sətirlərdə yox — bax `FolderRow.ask` şərhi.
  const dlg = useDialog();

  const system = folders.filter((f) => f.specialUse);
  const custom = folders.filter((f) => !f.specialUse);

  return (
    <nav className="folders" aria-label="Qovluqlar">
      {dlg.node}
      <div className="folders-top">
        <button className="btn-compose" onClick={onCompose}>
          Yaz
        </button>
        <button
          className="btn-icon"
          onClick={onRefresh}
          title="Yenilə"
          aria-label="Yenilə"
          disabled={refreshing}
        >
          <span className={refreshing ? 'spin' : ''}>
            <Icon name="refresh" size={17} />
          </span>
        </button>
      </div>

      <ul className="folder-list">
        {system.map((f) => (
          <FolderRow
            key={f.path}
            folder={f}
            selected={selected}
            onSelect={onSelect}
            ask={dlg.ask}
          />
        ))}
      </ul>

      {/*
        ⚠️ BAŞLIQ HƏMİŞƏ GÖRÜNÜR, qovluq olmasa da — «+» düyməsi orada
        yaşayır. Əvvəl bütün blok `custom.length > 0` şərtinə bağlı idi,
        yəni İLK qovluğu yaratmaq üçün heç bir yol yox idi.
      */}
      <div className="folders-heading folders-heading-row">
        <span>Mənim qovluqlarım</span>
        <button
          className="folder-add"
          onClick={() => {
            void dlg
              .ask({
                title: 'Yeni qovluq',
                confirmLabel: 'Yarat',
                input: { label: 'Qovluğun adı', placeholder: 'Müqavilələr' },
              })
              .then((name) => {
                // `null` — istifadəçi ləğv etdi.
                if (name === null) return;
                return api.createFolder(name).then(onFoldersChanged);
              })
              .catch((e: unknown) =>
                onError(
                  e instanceof ApiError && MSG[e.code] ? MSG[e.code]! : 'Qovluq yaradıla bilmədi.',
                ),
              );
          }}
          aria-label="Yeni qovluq"
          title="Yeni qovluq"
        >
          +
        </button>
      </div>
      {custom.length > 0 ? (
        <ul className="folder-list">
          {custom.map((f) => (
            <FolderRow
              key={f.path}
              folder={f}
              selected={selected}
              onSelect={onSelect}
              onFoldersChanged={onFoldersChanged}
              onError={onError}
              ask={dlg.ask}
            />
          ))}
        </ul>
      ) : (
        <p className="folders-empty">Hələ öz qovluğunuz yoxdur.</p>
      )}
      {/*
        YADDAŞ GÖSTƏRİCİSİ.
        ⚠️ Rəqəm YALNIZ limit varsa göstərilir. Limitsiz qutuda «0.4 MB /
        limitsiz» yazmaq mənasızdır və yer tutur.
      */}
      {quota && quota.limitBytes > 0 && (
        <div className="quota">
          <div className="quota-bar">
            <div
              className={`quota-fill${
                quota.usedBytes / quota.limitBytes >= 0.9
                  ? ' is-full'
                  : quota.usedBytes / quota.limitBytes >= 0.75
                    ? ' is-high'
                    : ''
              }`}
              style={{ width: `${Math.min(100, (quota.usedBytes / quota.limitBytes) * 100)}%` }}
            />
          </div>
          <span className="quota-text">
            {fmtBytes(quota.usedBytes)} / {fmtBytes(quota.limitBytes)}
          </span>
        </div>
      )}
    </nav>
  );
}

/** Xəta kodlarının izahı — server kodlarını istifadəçi dilinə çevirir. */
const MSG: Record<string, string> = {
  folder_empty: 'Ad boş ola bilməz.',
  folder_too_long: 'Ad 60 simvoldan uzun ola bilməz.',
  folder_separator: '«/» və «\\» işlədilə bilməz — alt qovluq hələ dəstəklənmir.',
  folder_control_chars: 'Adda yolverilməz simvol var.',
  folder_reserved: 'Bu ad sistem qovluğuna aiddir, başqa ad seçin.',
  folder_exists: 'Bu adda qovluq artıq var.',
  folder_system: 'Sistem qovluğu dəyişdirilə bilməz.',
};

function FolderRow({
  folder,
  selected,
  onSelect,
  onFoldersChanged,
  onError,
  ask,
}: {
  folder: Folder;
  selected: string;
  onSelect: (path: string) => void;
  /** Sistem qovluqlarında verilmir — orada düzəliş düymələri göstərilmir. */
  onFoldersChanged?: () => void;
  onError?: (msg: string) => void;
  /**
   * Dialoq açan funksiya — valideyndən gəlir.
   * ⚠️ Hook BURADA çağırıla bilməz: `FolderRow` siyahıda təkrarlanır və
   * hər sətir öz dialoq vəziyyətini saxlasa, eyni anda bir neçə dialoq
   * render olunardı.
   */
  ask: (spec: import('./Dialog').DialogSpec) => Promise<string | null>;
}): React.ReactElement {
  // Oxunmamış sayğacı yalnız mənalı olduğu yerdə göstərilir —
  // Göndərilənlər və Qaralamalarda mənasızdır (docs/05-panel-ui.md).
  /**
   * Sayğac qaydası.
   *
   * ⚠️ ÜMUMİ SAY DA GÖSTƏRİLİR — Yandex-dəki «967 / 1778» kimi.
   * Əvvəl yalnız oxunmamış say vardı və yalnız 0-dan böyük olanda; yəni
   * hər şeyi oxumuş istifadəçi qovluqda NEÇƏ məktub olduğunu heç yerdə
   * görmürdü. Qovluğun ölçüsü isə gündəlik lazım olan məlumatdır
   * («Arxivdə 4 min məktub var, ora atsam itməz?»).
   *
   * ⚠️ GÖNDƏRİLƏNLƏR/QARALAMALARDA OXUNMAMIŞ MƏNASIZDIR — özün yazmısan.
   * Orada yalnız ümumi say göstərilir (docs/05-panel-ui.md).
   */
  const isOwn = folder.specialUse === 'sent' || folder.specialUse === 'drafts';
  const showUnseen = folder.unseen > 0 && !isOwn;
  const showTotal = folder.total > 0;

  return (
    <li>
      <button
        className={`folder ${folder.path === selected ? 'is-active' : ''}`}
        onClick={() => onSelect(folder.path)}
        aria-current={folder.path === selected ? 'page' : undefined}
      >
        <span className="folder-icon" aria-hidden="true">
          <Icon name={folder.specialUse ? ICONS[folder.specialUse] : 'folder'} size={18} />
        </span>
        <span className="folder-name">{label(folder)}</span>
        {(showUnseen || showTotal) && (
          <span className="folder-nums">
            {showUnseen && <span className="folder-count">{fmtCount(folder.unseen)}</span>}
            {/*
              ⚠️ ÜMUMİ SAY SÖNÜKDÜR VƏ AYIRICI İLƏ. Eyni qalınlıqda olsa,
              iki rəqəm bir-birinə qarışır və «967 1778» oxunmaz olur.
            */}
            {showTotal && (
              <span className="folder-total">
                {showUnseen ? ' / ' : ''}
                {fmtCount(folder.total)}
              </span>
            )}
          </span>
        )}
      </button>

      {/*
        DÜZƏLİŞ VƏ SİLMƏ — yalnız istifadəçinin öz qovluqlarında.
        ⚠️ Qovluq düyməsinin İÇİNDƏ ola bilməzlər (düymə-içində-düymə
        etibarsızdır), ona görə qardaş element kimi qoyulur.
      */}
      {onFoldersChanged && onError && (
        <span className="folder-tools">
          <button
            className="folder-tool"
            aria-label={`${folder.name} — adını dəyiş`}
            title="Adını dəyiş"
            onClick={(e) => {
              e.stopPropagation();
              void ask({
                title: 'Qovluğun adını dəyiş',
                confirmLabel: 'Dəyiş',
                input: { label: 'Yeni ad', initial: folder.name },
              })
                .then((name) => {
                  if (name === null || name === folder.name) return;
                  return api.renameFolder(folder.path, name).then(onFoldersChanged);
                })
                .catch((err: unknown) =>
                  onError(
                    err instanceof ApiError && MSG[err.code] ? MSG[err.code]! : 'Ad dəyişdirilmədi.',
                  ),
                );
            }}
          >
            <Icon name="draft" size={14} />
          </button>
          <button
            className="folder-tool is-danger"
            aria-label={`${folder.name} — sil`}
            title="Sil"
            onClick={(e) => {
              e.stopPropagation();
              // ⚠️ MESAJ SAYI GÖSTƏRİLİR. «Silmək istəyirsiniz?» sualı
              // içində 400 məktub olduğunu demir və istifadəçi «hə» basır.
              void ask({
                title: `«${folder.name}» qovluğu silinsin?`,
                body:
                  folder.total > 0
                    ? `İçindəki ${folder.total} məktub da HƏMİŞƏLİK silinəcək. Geri qaytarmaq mümkün olmayacaq.`
                    : 'Qovluq boşdur.',
                confirmLabel: 'Sil',
                danger: true,
              })
                .then((ok) => {
                  if (ok === null) return;
                  return api.deleteFolder(folder.path).then(onFoldersChanged);
                })
                .catch((err: unknown) =>
                  onError(
                    err instanceof ApiError && MSG[err.code] ? MSG[err.code]! : 'Silinmədi.',
                  ),
                );
            }}
          >
            <Icon name="trash" size={14} />
          </button>
        </span>
      )}
    </li>
  );
}


/** Qovluğun göstəriləcək adı — sistemdirsə tərcümə, deyilsə öz adı. */
export function folderLabel(f: Folder): string {
  return f.specialUse ? FOLDER_LABELS[f.specialUse] : f.name;
}
